import '@material/web/icon/icon.js'
import '@operato/i18n/ox-i18n.js'

import { css, html, PropertyValues, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { OxPropertyEditor, PropertySpec } from '@operato/property-editor'
import { ScrollbarStyles } from '@operato/styles/scrollbar-styles.js'
import { Component } from '@hatiolab/things-scene'

@customElement('property-editor-gltf-info')
export default class GLTFInfoEditor extends OxPropertyEditor {
  static styles = [
    ...OxPropertyEditor.styles,
    ScrollbarStyles,
    css`
      [info-panel] {
        font-size: 12px;
        padding: 4px 0;
      }

      .dim-row {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 3px 0;
      }

      .dim-label {
        min-width: 65px;
        font-size: 11px;
        color: var(--md-sys-color-on-surface-variant, #666);
      }

      .dim-value {
        font-family: monospace;
        font-size: 12px;
        font-weight: 500;
      }

      .dim-value.mismatch {
        color: var(--md-sys-color-error, #b3261e);
      }

      .dim-value.match {
        color: var(--md-sys-color-primary, #6750a4);
      }

      .ratio-info {
        font-size: 10px;
        color: var(--md-sys-color-on-surface-variant, #888);
        padding: 2px 0;
      }

      .button-row {
        display: flex;
        gap: 4px;
        margin-top: 6px;
      }

      .action-btn {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 6px 8px;
        border: 1px solid var(--md-sys-color-outline-variant, rgba(0, 0, 0, 0.2));
        border-radius: 6px;
        background: var(--md-sys-color-surface-container, #f3edf7);
        cursor: pointer;
        font-size: 11px;
        color: var(--md-sys-color-on-surface, #1c1b1f);
        transition: background 0.15s;
      }

      .action-btn:hover {
        background: var(--md-sys-color-surface-container-highest, #e6e0e9);
      }

      .action-btn md-icon {
        --md-icon-size: 16px;
      }

      .ratio-lock-toggle {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        cursor: pointer;
        color: var(--md-sys-color-on-surface, #1c1b1f);
      }

      .ratio-lock-toggle input[type='checkbox'] {
        accent-color: var(--md-sys-color-primary, #6750a4);
        margin: 0;
      }

      .stats-row {
        display: flex;
        gap: 12px;
        padding: 4px 0;
        margin-top: 4px;
        border-top: 1px solid var(--md-sys-color-outline-variant, rgba(0, 0, 0, 0.1));
      }

      .stat-item {
        display: flex;
        align-items: center;
        gap: 3px;
        font-size: 11px;
        color: var(--md-sys-color-on-surface-variant, #666);
      }

      .stat-item md-icon {
        --md-icon-size: 13px;
      }

      .stat-value {
        font-weight: 600;
        color: var(--md-sys-color-on-surface, #1c1b1f);
      }
    `
  ]

  @property({ type: String }) declare src: string | undefined

  private _component: any = null

  @state() declare width: number
  @state() declare height: number
  @state() declare depth: number
  @state() declare currentWidth: number
  @state() declare currentHeight: number
  @state() declare currentDepth: number
  @state() declare meshCount: number
  @state() declare vertexCount: number
  @state() declare animationCount: number
  @state() declare materialCount: number

  constructor() {
    super()
    this.width = 0
    this.height = 0
    this.depth = 0
    this.currentWidth = 0
    this.currentHeight = 0
    this.currentDepth = 0
    this.meshCount = 0
    this.vertexCount = 0
    this.animationCount = 0
    this.materialCount = 0
  }

  editorTemplate(value: any, spec: PropertySpec): TemplateResult {
    const valid = this.width && this.height && this.depth

    var property = spec.property || {}
    var { action } = property

    const isProportional = valid && this._isProportional()

    return html`
      <fieldset fullwidth>
        <div info-panel>
          ${valid
            ? html`
                <div class="dim-row">
                  <span class="dim-label">Model</span>
                  <span class="dim-value">${this.width} × ${this.height} × ${this.depth}</span>
                </div>
                <div class="dim-row">
                  <span class="dim-label">Current</span>
                  <span class="dim-value ${isProportional ? 'match' : 'mismatch'}">
                    ${this.currentWidth} × ${this.currentHeight} × ${this.currentDepth}
                  </span>
                </div>

                <div class="stats-row">
                  <span class="stat-item"><md-icon>view_in_ar</md-icon> <span class="stat-value">${this.meshCount}</span> meshes</span>
                  <span class="stat-item"><md-icon>scatter_plot</md-icon> <span class="stat-value">${this._formatNumber(this.vertexCount)}</span> verts</span>
                  <span class="stat-item"><md-icon>palette</md-icon> <span class="stat-value">${this.materialCount}</span> mats</span>
                  ${this.animationCount > 0
                    ? html`<span class="stat-item"><md-icon>animation</md-icon> <span class="stat-value">${this.animationCount}</span> anims</span>`
                    : ''}
                </div>

                <div class="button-row">
                  <div
                    class="action-btn"
                    title="W,H 중 짧은 쪽 기준으로 원본 비율 적용"
                    @click=${() => this._applyProportional(action)}
                  >
                    <md-icon>aspect_ratio</md-icon>
                    <ox-i18n msgid="label.fit-ratio">Fit Ratio</ox-i18n>
                  </div>
                  <label class="ratio-lock-toggle">
                    <input
                      type="checkbox"
                      .checked=${this._getRatioLock()}
                      @change=${(e: Event) => {
                        e.stopPropagation()
                        this._setRatioLock((e.target as HTMLInputElement).checked)
                      }}
                    />
                    <md-icon style="--md-icon-size:14px">lock</md-icon>
                    Ratio Lock
                  </label>
                </div>
              `
            : html`<div class="dim-row"><span class="dim-label">Loading...</span></div>`}
        </div>
      </fieldset>
    `
  }

  private _formatNumber(n: number): string {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
    return String(n)
  }

  private _isProportional(): boolean {
    if (!this.width || !this.height || !this.depth) return true
    if (!this.currentWidth || !this.currentHeight || !this.currentDepth) return true

    const rw = this.currentWidth / this.width
    const rh = this.currentHeight / this.height
    const rd = this.currentDepth / this.depth
    const avg = (rw + rh + rd) / 3
    const tolerance = 0.05

    return Math.abs(rw - avg) / avg < tolerance && Math.abs(rh - avg) / avg < tolerance && Math.abs(rd - avg) / avg < tolerance
  }

  private _applyAction(action: any, dimension: { width: number; height: number; depth: number }) {
    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            typeof action === 'function' && action(selected[0], dimension)
            this._refreshCurrentSize(selected[0])
          }
        }
      })
    )
  }

  /**
   * W, H 중 짧은 쪽을 기준으로 원래 모델의 비율에 맞게 치수를 조절한다.
   * (contain 방식 — 모델이 컴포넌트 영역 안에 들어감)
   */
  private _applyProportional(action: any) {
    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            const component = selected[0]
            if (!component || typeof action !== 'function') return

            const { width: cw = 1, height: ch = 1, depth: cd = 1 } = component.state
            const { width: ow, height: oh, depth: od } = this // 원래 모델 치수

            if (!ow || !oh || !od) return

            // 이미 비율이 맞으면 스킵
            if (this._isProportional()) return

            // W, H 중 ratio가 작은 축을 기준
            const scale = Math.min(cw / ow, ch / oh)

            action(component, {
              width: Math.round(ow * scale),
              height: Math.round(oh * scale),
              depth: Math.round(od * scale)
            })

            this._refreshCurrentSize(component)
          }
        }
      })
    )
  }

  private _getRatioLock(): boolean {
    return !!this._component?.state?.ratioLock
  }

  private _setRatioLock(value: boolean) {
    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            selected[0].set('ratioLock', value)
            this.requestUpdate()
          }
        }
      })
    )
  }

  private _refreshCurrentSize(component: any) {
    if (!component) return
    const state = component.state || {}
    this.currentWidth = Math.round(state.width || 0)
    this.currentHeight = Math.round(state.height || 0)
    this.currentDepth = Math.round(state.depth || 0)
  }

  updated(changes: PropertyValues<this>) {
    if (changes.has('src')) {
      this.dispatchEvent(
        new CustomEvent('i-need-selected', {
          bubbles: true,
          composed: true,
          detail: {
            callback: async (selected: any[]) => {
              const component = selected[0]
              this._component = component
              this._refreshCurrentSize(component)
              await this.fetchSourceInfo(component, this.src!)
            }
          }
        })
      )
    }
  }

  private _pollTimer?: number

  async fetchSourceInfo(component: Component, src: string) {
    if (!src || !src.trim()) {
      return
    }

    // 이미 로드된 realObject에서 직접 정보를 읽는다
    if (this._tryReadFromRealObject(component)) {
      return
    }

    // GLTF 비동기 로드 중이면 폴링 (최대 20회 = 10초)
    if (this._pollTimer) clearInterval(this._pollTimer)
    let retries = 0
    this._pollTimer = window.setInterval(() => {
      if (this._tryReadFromRealObject(component) || ++retries >= 20) {
        clearInterval(this._pollTimer)
        this._pollTimer = undefined
      }
    }, 500)
  }

  private _tryReadFromRealObject(component: any): boolean {
    const ro = component?.realObject
    if (!ro?.objectSize) return false

    const { x = 0, y = 0, z = 0 } = ro.objectSize
    this.width = Math.round(x * 100) / 100
    this.depth = Math.round(y * 100) / 100
    this.height = Math.round(z * 100) / 100

    // 통계: pivot 또는 object3d에서 수집
    const root = ro.pivot || ro.object3d
    if (root) {
      let meshes = 0
      let vertices = 0
      const materials = new Set()

      root.traverse((node: any) => {
        if (node.isMesh) {
          meshes++
          if (node.geometry) {
            const pos = node.geometry.getAttribute('position')
            if (pos) vertices += pos.count
          }
          if (node.material) {
            const mats = Array.isArray(node.material) ? node.material : [node.material]
            mats.forEach((m: any) => materials.add(m.uuid))
          }
        }
      })

      this.meshCount = meshes
      this.vertexCount = vertices
      this.materialCount = materials.size
    }

    this.animationCount = ro._animationActions?.size || 0

    return true
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this._pollTimer) clearInterval(this._pollTimer)
  }
}
